import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Send, Copy, Download, Link2, MoreHorizontal, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
    "Not Sent": "text-gray-500",
    "Sent (via Realloc)": "text-blue-300",
    "Sent (via External)": "text-blue-300",
    "Email sent": "text-blue-300",
    "In Progress": "text-amber-300",
    "Send Failed": "text-red-300",
    "Submitted": "text-emerald-300",
    "Revoked": "text-gray-500 line-through",
    "Expired": "text-gray-500 line-through",
};

export default function DistributeTab() {
    const { api, user } = useAuth();
    const [params] = useSearchParams();
    const wsId = params.get("ws") || user?.enterprise_id;

    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [activity, setActivity] = useState([]);
    const [showSendModal, setShowSendModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [showAddCandidate, setShowAddCandidate] = useState(false);
    const [showImportCsv, setShowImportCsv] = useState(false);
    const [generatedLinks, setGeneratedLinks] = useState(null); // {generated:[{full_name,email,link_url,expires_at}], skipped:[...]}
    const [filter, setFilter] = useState("");

    const refreshCandidates = () => {
        const q = selectedAssessment ? `?assessment_id=${selectedAssessment}` : "";
        api.get(`/v2/workspaces/${wsId}/candidates/distribution-status${q}`)
            .then(r => setCandidates(r.data.candidates || []))
            .catch(() => {});
    };
    const refreshActivity = () => {
        api.get(`/v2/workspaces/${wsId}/assessments/distribution-activity`)
            .then(r => setActivity(r.data.events || []))
            .catch(() => {});
    };

    useEffect(() => {
        if (!wsId) return;
        api.get(`/v2/workspaces/${wsId}/assessments/distributable`)
            .then(r => setAssessments(r.data.assessments || []))
            .catch(() => {});
        refreshActivity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wsId]);

    useEffect(() => {
        if (wsId) refreshCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAssessment, wsId]);

    const filtered = useMemo(() => {
        const q = filter.toLowerCase();
        return candidates.filter(c =>
            !q || (c.full_name || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q) ||
            (c.department || "").toLowerCase().includes(q));
    }, [candidates, filter]);

    const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.candidate_id));
    const toggleAll = () => {
        const next = new Set(selectedIds);
        if (allFilteredSelected) filtered.forEach(c => next.delete(c.candidate_id));
        else filtered.forEach(c => next.add(c.candidate_id));
        setSelectedIds(next);
    };
    const toggleOne = id => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };

    const generateLinks = async (regenerate = false) => {
        if (!selectedAssessment || selectedIds.size === 0) return;
        try {
            // Step 1: Mint new tokens for candidates without an active link.
            const r = await api.post(
                `/v2/workspaces/${wsId}/assessments/${selectedAssessment}/generate-links`,
                { candidate_ids: Array.from(selectedIds), regenerate_existing: regenerate }
            );
            const newCount = r.data.generated.length;
            const skipCount = r.data.skipped.length;
            // Step 2: Fetch full link list for the selected candidates (covers both newly-
            // generated AND already-active links). This is what the partner actually wants
            // to see + copy.
            const exp = await api.post(
                `/v2/workspaces/${wsId}/assessments/${selectedAssessment}/export-links`,
                { candidate_ids: Array.from(selectedIds), format: "json" }
            );
            toast.success(
                `${newCount} new${skipCount ? ` · ${skipCount} reused existing` : ""} — showing ${exp.data.rows.length} link${exp.data.rows.length === 1 ? "" : "s"}`
            );
            setGeneratedLinks({ rows: exp.data.rows, new_count: newCount, skip_count: skipCount });
            refreshCandidates(); refreshActivity();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generate failed");
        }
    };

    const exportCsv = async () => {
        try {
            const r = await api.post(
                `/v2/workspaces/${wsId}/assessments/${selectedAssessment}/export-links`,
                { candidate_ids: Array.from(selectedIds), format: "csv" },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `realloc-links-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${selectedIds.size} links`);
            refreshCandidates(); refreshActivity();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export failed");
        }
    };

    const copyAll = async () => {
        try {
            const r = await api.post(
                `/v2/workspaces/${wsId}/assessments/${selectedAssessment}/export-links`,
                { candidate_ids: Array.from(selectedIds), format: "json" }
            );
            const lines = r.data.rows.map(x => `${x.full_name} — ${x.email} — ${x.assessment_link}`).join("\n");
            await navigator.clipboard.writeText(lines);
            toast.success(`Copied ${r.data.rows.length} links`);
            refreshCandidates(); refreshActivity();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Copy failed");
        }
    };

    if (!wsId) {
        return <div className="bg-card border border-border/40 p-6 text-sm text-gray-500">No workspace context. Add <code className="font-mono text-xs">?ws=...</code> to the URL or log in as an enterprise user.</div>;
    }

    const selectedAsm = assessments.find(a => a.id === selectedAssessment);
    const canSendEmail = Array.from(selectedIds).every(id => {
        const c = candidates.find(c => c.candidate_id === id);
        return c?.has_email;
    });

    return (
        <div data-testid="distribute-tab">
            {/* Section A — Pick assessment */}
            <div className="bg-card border border-border/40 p-5 mb-4">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-body">1. Pick the assessment</p>
                <select className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                    value={selectedAssessment} onChange={e => setSelectedAssessment(e.target.value)}
                    data-testid="select-assessment">
                    <option value="">Select an assessment…</option>
                    {assessments.map(a => {
                        const looksLikeEmail = (a.job_title || "").includes("@");
                        const label = looksLikeEmail
                            ? `Manual upload · ${a.created_at?.slice(0, 10) || "unknown date"}`
                            : (a.job_title || "Untitled assessment");
                        return (
                            <option key={a.id} value={a.id}>{label} · {a.source}{a.parse_confidence ? ` (${Math.round(a.parse_confidence)}%)` : ""}</option>
                        );
                    })}
                </select>
                {selectedAsm && (
                    <p className="text-xs text-gray-500 mt-2 font-mono">
                        {selectedAsm.module_count} modules · est. 30 min · source: {selectedAsm.source}
                    </p>
                )}
            </div>

            {/* Section B — Pick candidates */}
            <div className="bg-card border border-border/40 mb-4">
                <div className="flex items-center justify-between p-5 border-b border-border/40 gap-4">
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-body">2. Pick candidates</p>
                        <p className="text-xs text-gray-600 mt-1 font-mono">{filtered.length} of {candidates.length} candidates · {selectedIds.size} selected</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => setShowAddCandidate(true)}
                            className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white hover:border-white/30 px-3 py-1.5"
                            data-testid="add-candidate-btn">
                            + Add candidate
                        </button>
                        <button onClick={() => setShowImportCsv(true)}
                            className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white hover:border-white/30 px-3 py-1.5"
                            data-testid="import-csv-btn">
                            Import CSV
                        </button>
                        <input className="bg-muted border border-border/40 px-3 py-1.5 text-xs text-white w-56"
                            placeholder="Filter by name, email, or department"
                            value={filter} onChange={e => setFilter(e.target.value)}
                            data-testid="candidate-filter" />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-border/40 text-gray-600 uppercase tracking-wider">
                                <th className="px-5 py-3 text-left w-10">
                                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll}
                                        data-testid="select-all-candidates" />
                                </th>
                                <th className="px-5 py-3 text-left">Name</th>
                                <th className="px-5 py-3 text-left">Email</th>
                                <th className="px-5 py-3 text-left">Role</th>
                                <th className="px-5 py-3 text-left">Department</th>
                                <th className="px-5 py-3 text-left">Link</th>
                                <th className="px-5 py-3 text-left">Status</th>
                                <th className="px-5 py-3 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(c => (
                                <tr key={c.candidate_id} className="border-b border-border/20 hover:bg-white/[0.02]"
                                    data-testid={`candidate-row-${c.candidate_id}`}>
                                    <td className="px-5 py-3">
                                        <input type="checkbox" checked={selectedIds.has(c.candidate_id)}
                                            onChange={() => toggleOne(c.candidate_id)}
                                            data-testid={`candidate-cb-${c.candidate_id}`} />
                                    </td>
                                    <td className="px-5 py-3 text-white">{c.full_name}</td>
                                    <td className="px-5 py-3 text-gray-400 font-mono">{c.email || <span className="text-amber-400">no email</span>}</td>
                                    <td className="px-5 py-3 text-gray-400">{c.job_title || "—"}</td>
                                    <td className="px-5 py-3 text-gray-400">{c.department || "—"}</td>
                                    <td className="px-5 py-3 text-gray-400 font-mono">{c.link_status}</td>
                                    <td className={`px-5 py-3 font-mono ${STATUS_COLORS[c.assessment_status] || "text-gray-400"}`}>{c.assessment_status}</td>
                                    <td className="px-5 py-3">
                                        <RowMenu c={c} api={api} wsId={wsId} onChange={() => { refreshCandidates(); refreshActivity(); }} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="px-5 py-10 text-xs text-gray-500 text-center">
                            {candidates.length === 0 ? "No candidates in this workspace yet." : "No candidates match your filter."}
                        </div>
                    )}
                </div>
            </div>

            {/* Section C — Action bar */}
            <div className="sticky bottom-0 bg-bg/95 backdrop-blur border-t border-border/40 -mx-8 px-8 py-4 flex items-center justify-between"
                data-testid="action-bar">
                <p className="text-xs text-gray-500 font-mono">
                    {selectedIds.size > 0
                        ? `${selectedIds.size} selected · assessment: ${selectedAsm?.job_title || "—"}`
                        : "Pick an assessment + at least one candidate to enable distribution."}
                </p>
                <div className="flex gap-2">
                    <button onClick={() => generateLinks(false)}
                        disabled={!selectedAssessment || selectedIds.size === 0}
                        className="px-4 py-2 text-sm border border-border/60 text-gray-300 hover:text-white hover:border-white disabled:opacity-30 inline-flex items-center gap-2"
                        data-testid="generate-links-btn">
                        <Link2 className="w-4 h-4" /> Generate Links
                    </button>
                    <button onClick={() => setShowSendModal(true)}
                        disabled={!selectedAssessment || selectedIds.size === 0 || !canSendEmail}
                        title={!canSendEmail ? "Some candidates have no email" : ""}
                        className="px-4 py-2 text-sm bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 disabled:opacity-30 inline-flex items-center gap-2"
                        data-testid="send-realloc-btn">
                        <Send className="w-4 h-4" /> Send via Realloc
                    </button>
                    <button onClick={() => setShowCopyModal(true)}
                        disabled={!selectedAssessment || selectedIds.size === 0}
                        className="px-4 py-2 text-sm bg-white text-black hover:bg-gray-200 disabled:opacity-30 inline-flex items-center gap-2"
                        data-testid="copy-export-btn">
                        <Download className="w-4 h-4" /> Copy / Export
                    </button>
                </div>
            </div>

            {/* Activity log */}
            <div className="bg-card border border-border/40 mt-6" data-testid="activity-log">
                <div className="px-5 py-3 border-b border-border/40">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-body">Recent activity</p>
                </div>
                {activity.length === 0 && <div className="px-5 py-6 text-xs text-gray-500 text-center">No distribution events yet.</div>}
                {activity.map(e => (
                    <div key={e.id} className="px-5 py-2.5 border-b border-border/20 last:border-0 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                            <span className="text-gray-500 font-mono w-44">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                            <span className="text-white font-mono">{e.event_type.replace(/_/g, ' ')}</span>
                            <span className="text-gray-500">{e.candidate_count} candidates · {e.triggered_by}</span>
                        </div>
                    </div>
                ))}
            </div>

            {showSendModal && (
                <SendModal
                    api={api} wsId={wsId} assessmentId={selectedAssessment}
                    selected={Array.from(selectedIds)} onClose={() => setShowSendModal(false)}
                    onSent={() => { refreshCandidates(); refreshActivity(); setShowSendModal(false); setSelectedIds(new Set()); }}
                />
            )}
            {showCopyModal && (
                <CopyExportModal
                    onClose={() => setShowCopyModal(false)}
                    onCopy={async () => { await copyAll(); setShowCopyModal(false); }}
                    onExport={async () => { await exportCsv(); setShowCopyModal(false); }}
                    count={selectedIds.size}
                />
            )}
            {showAddCandidate && (
                <AddCandidateModal api={api} wsId={wsId}
                    onClose={() => setShowAddCandidate(false)}
                    onCreated={() => { refreshCandidates(); setShowAddCandidate(false); }} />
            )}
            {showImportCsv && (
                <ImportCsvModal api={api} wsId={wsId}
                    onClose={() => setShowImportCsv(false)}
                    onImported={() => { refreshCandidates(); setShowImportCsv(false); }} />
            )}
            {generatedLinks && (
                <GeneratedLinksModal data={generatedLinks}
                    onClose={() => setGeneratedLinks(null)} />
            )}
        </div>
    );
}


function RowMenu({ c, api, wsId, onChange }) {
    const [open, setOpen] = useState(false);
    const callRevoke = async () => {
        if (!c.instance_id) return;
        if (!window.confirm("Revoke this link? The candidate's link will stop working immediately.")) return;
        try {
            await api.post(`/v2/instances/${c.instance_id}/revoke-link`);
            toast.success("Link revoked");
            onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
        setOpen(false);
    };
    const callReset = async () => {
        if (!c.instance_id) return;
        if (!window.confirm("Reset assessment? Existing responses will be cleared and a new link issued.")) return;
        try {
            await api.post(`/v2/instances/${c.instance_id}/reset`);
            toast.success("Assessment reset · new link issued");
            onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
        setOpen(false);
    };
    if (!c.instance_id) return null;
    return (
        <div className="relative">
            <button onClick={() => setOpen(!open)} className="text-gray-500 hover:text-white"
                data-testid={`row-menu-${c.candidate_id}`}>
                <MoreHorizontal className="w-4 h-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-6 bg-card border border-border/60 z-10 w-48 shadow-lg" onMouseLeave={() => setOpen(false)}>
                    <button onClick={callRevoke} className="block w-full px-4 py-2 text-xs text-left text-red-300 hover:bg-white/5 flex items-center gap-2"
                        data-testid={`revoke-link-btn-${c.candidate_id}`}>
                        <X className="w-3 h-3" /> Revoke link
                    </button>
                    <button onClick={callReset} className="block w-full px-4 py-2 text-xs text-left text-amber-300 hover:bg-white/5 flex items-center gap-2"
                        data-testid={`reset-assessment-btn-${c.candidate_id}`}>
                        <RotateCcw className="w-3 h-3" /> Reset assessment
                    </button>
                </div>
            )}
        </div>
    );
}


function SendModal({ api, wsId, assessmentId, selected, onClose, onSent }) {
    const [subject, setSubject] = useState("{{company_name}} — Workforce assessment from {{operator_name}}");
    const [body, setBody] = useState(`Hi {{candidate_first_name}},

You've been invited to complete a workforce assessment from {{company_name}}.

This assessment takes approximately {{estimated_minutes}} minutes to complete and you can save and return at any point before {{expires_at}}.

Click below to begin:

{{link}}

If you have any questions, reach out to {{operator_name}} at {{operator_email}}.`);
    const [sendFrom, setSendFrom] = useState("realloc");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post(
                `/v2/workspaces/${wsId}/assessments/${assessmentId}/send-emails`,
                { candidate_ids: selected, subject, body, send_from: sendFrom }
            );
            const sent = r.data.results.filter(x => x.status === "sent").length;
            const failed = r.data.results.length - sent;
            if (sent > 0) toast.success(`Sent ${sent} emails${failed ? ` · ${failed} failed` : ""}`);
            else toast.error("All sends failed. Try Copy / Export as a fallback.");
            onSent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-card border border-border/40 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()} data-testid="send-modal">
                <div className="p-6 border-b border-border/40">
                    <h2 className="text-xl font-display font-bold text-white">Send via Realloc Email</h2>
                    <p className="text-xs text-gray-500 mt-1 font-mono">{selected.length} candidates · template variables: {`{{candidate_first_name}}, {{company_name}}, {{operator_name}}, {{link}}, {{expires_at}}`}</p>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Subject</label>
                        <input className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                            value={subject} onChange={e => setSubject(e.target.value)}
                            data-testid="send-subject" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Body</label>
                        <textarea className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white font-mono"
                            rows={10} value={body} onChange={e => setBody(e.target.value)}
                            data-testid="send-body" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Send from</label>
                        <select className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                            value={sendFrom} onChange={e => setSendFrom(e.target.value)} data-testid="send-from">
                            <option value="realloc">Realloc default sender (teamsupport@thcohq.com)</option>
                            <option value="operator">My email (reply-to)</option>
                        </select>
                    </div>
                </div>
                <div className="p-6 border-t border-border/40 flex justify-end gap-3">
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy}
                        className="bg-white text-black px-4 py-2 text-sm font-semibold disabled:opacity-40"
                        data-testid="send-confirm-btn">
                        {busy ? "Sending…" : `Send ${selected.length} emails`}
                    </button>
                </div>
            </div>
        </div>
    );
}


function CopyExportModal({ onClose, onCopy, onExport, count }) {
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-card border border-border/40 w-full max-w-md p-6" onClick={e => e.stopPropagation()}
                data-testid="copy-export-modal">
                <h2 className="text-xl font-display font-bold text-white mb-2">Copy / Export Links</h2>
                <p className="text-xs text-gray-500 mb-5">{count} candidates · choose a format</p>
                <div className="space-y-2">
                    <button onClick={onCopy}
                        className="w-full text-left px-4 py-3 border border-border/60 hover:border-white text-white flex items-center gap-3"
                        data-testid="copy-all-btn">
                        <Copy className="w-4 h-4" />
                        <div>
                            <p className="text-sm">Copy all to clipboard</p>
                            <p className="text-xs text-gray-500">"Name — Email — Link" one per line. Paste into mail-merge or rich text email.</p>
                        </div>
                    </button>
                    <button onClick={onExport}
                        className="w-full text-left px-4 py-3 border border-border/60 hover:border-white text-white flex items-center gap-3"
                        data-testid="export-csv-btn">
                        <Download className="w-4 h-4" />
                        <div>
                            <p className="text-sm">Download CSV</p>
                            <p className="text-xs text-gray-500">Columns: full_name, email, role, department, assessment_link, link_expires_at</p>
                        </div>
                    </button>
                </div>
                <div className="flex justify-end mt-5 pt-4 border-t border-border/30">
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Close</button>
                </div>
            </div>
        </div>
    );
}



function AddCandidateModal({ api, wsId, onClose, onCreated }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [form, setForm] = useState({
        full_name: "", email: "", job_title: "", department: "",
        region: "", business_unit: "", employee_id: "", manager_email: "", manager_name: "",
    });
    const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const submit = async () => {
        if (!form.full_name.trim() || !form.email.trim()) {
            setErr("Name and email are required.");
            return;
        }
        setErr(null); setBusy(true);
        try {
            const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, (v || "").trim() || null]));
            payload.full_name = form.full_name.trim();
            payload.email = form.email.trim();
            await api.post(`/v2/workspaces/${wsId}/candidates`, payload);
            toast.success(`Added ${form.full_name}`);
            onCreated();
        } catch (e) {
            setErr(e.response?.data?.detail || "Could not add candidate.");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-bg border border-border w-full max-w-xl" onClick={e => e.stopPropagation()}
                data-testid="add-candidate-modal">
                <div className="flex items-baseline justify-between p-5 border-b border-border/40">
                    <h3 className="text-base font-display font-bold text-white">Add candidate</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    {[
                        ["full_name", "Full name *"],
                        ["email", "Email *"],
                        ["job_title", "Job title"],
                        ["department", "Department"],
                        ["region", "Region"],
                        ["business_unit", "Business unit"],
                        ["employee_id", "Employee ID"],
                        ["manager_email", "Manager email"],
                        ["manager_name", "Manager name"],
                    ].map(([k, label]) => (
                        <div key={k} className={k === "full_name" || k === "email" ? "col-span-2" : ""}>
                            <label className="text-xs text-gray-500 uppercase tracking-wider font-mono block mb-1">{label}</label>
                            <input value={form[k]} onChange={f(k)}
                                className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                data-testid={`add-cand-${k}`} />
                        </div>
                    ))}
                </div>
                {err && <p className="px-5 pb-2 text-xs text-red-300" data-testid="add-cand-error">{err}</p>}
                <div className="flex justify-end gap-3 p-5 border-t border-border/40">
                    <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-white">Cancel</button>
                    <button onClick={submit} disabled={busy}
                        className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200 disabled:opacity-40"
                        data-testid="add-cand-submit">
                        {busy ? "Adding…" : "Add candidate"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function ImportCsvModal({ api, wsId, onClose, onImported }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    const inputRef = useRef(null);

    const downloadTemplate = async () => {
        try {
            const r = await api.get(`/v2/workspaces/${wsId}/candidates/csv-template`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = url; a.download = "realloc-candidates-template.csv"; a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not download template.");
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        setErr(null); setBusy(true); setResult(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post(`/v2/admin/assessments/onboard/${wsId}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(`Imported ${r.data.candidates_created} candidates`);
        } catch (e) {
            setErr(e.response?.data?.detail || "CSV import failed.");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-bg border border-border w-full max-w-xl" onClick={e => e.stopPropagation()}
                data-testid="import-csv-modal">
                <div className="flex items-baseline justify-between p-5 border-b border-border/40">
                    <h3 className="text-base font-display font-bold text-white">Import candidates from CSV</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-xs text-gray-400 font-body leading-relaxed">
                        CSV columns: <span className="font-mono text-gray-300">full_name, email, job_title, department, region, business_unit, employee_id, manager_email, manager_name</span>.
                        Only <span className="font-mono">full_name</span> and <span className="font-mono">email</span> are required.
                    </p>
                    <button onClick={downloadTemplate}
                        className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white px-3 py-1.5"
                        data-testid="csv-template-download">
                        ↓ Download CSV template
                    </button>
                    <div className="border border-dashed border-border/60 p-6 text-center">
                        <input ref={inputRef} type="file" accept=".csv" id="csv-input" className="hidden"
                            onChange={e => handleFile(e.target.files?.[0])}
                            data-testid="csv-import-input" />
                        <label htmlFor="csv-input"
                            className={`text-sm cursor-pointer ${busy ? "text-gray-600" : "text-gray-300 hover:text-white"}`}>
                            {busy ? "Importing…" : "Click to choose a CSV file"}
                        </label>
                    </div>
                    {result && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200" data-testid="csv-import-result">
                            <p className="font-mono">{result.candidates_created} candidates imported.</p>
                            {result.errors?.length > 0 && (
                                <p className="mt-1 text-amber-200">{result.errors.length} row error(s): {result.errors.slice(0, 3).join("; ")}</p>
                            )}
                        </div>
                    )}
                    {err && <p className="text-xs text-red-300" data-testid="csv-import-error">{err}</p>}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-border/40">
                    <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-white">Close</button>
                    {result && (
                        <button onClick={onImported}
                            className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200"
                            data-testid="csv-import-done">
                            Done — refresh candidate list
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}



function GeneratedLinksModal({ data, onClose }) {
    const rows = data.rows || [];
    const [copiedIdx, setCopiedIdx] = useState(null);

    const copyOne = async (link, idx) => {
        try {
            await navigator.clipboard.writeText(link);
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1500);
        } catch (e) {
            toast.error("Could not copy. Select the text manually.");
        }
    };

    const copyAll = async () => {
        try {
            const text = rows.map(r => `${r.full_name} — ${r.email} — ${r.assessment_link}`).join("\n");
            await navigator.clipboard.writeText(text);
            toast.success(`Copied ${rows.length} link${rows.length === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error("Could not copy. Select the text manually.");
        }
    };

    const downloadCsv = () => {
        const header = "full_name,email,role,department,assessment_link,link_expires_at\n";
        const body = rows.map(r => [
            r.full_name, r.email, r.role || "", r.department || "",
            r.assessment_link, r.link_expires_at || "",
        ].map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([header + body], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `realloc-links-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded ${rows.length} link${rows.length === 1 ? "" : "s"}`);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-bg border border-border w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}
                data-testid="generated-links-modal">
                <div className="flex items-baseline justify-between p-5 border-b border-border/40">
                    <div>
                        <h3 className="text-base font-display font-bold text-white">Candidate links</h3>
                        <p className="text-xs text-gray-500 font-body mt-1">
                            {rows.length} link{rows.length === 1 ? "" : "s"} ready to share.
                            {data.new_count > 0 && <> <span className="text-emerald-300 font-mono">{data.new_count} newly generated</span>.</>}
                            {data.skip_count > 0 && <> <span className="text-amber-300 font-mono">{data.skip_count} reused</span>.</>}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
                </div>

                <div className="flex items-center gap-2 px-5 py-3 border-b border-border/30">
                    <button onClick={copyAll}
                        className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5"
                        data-testid="copy-all-links-btn">
                        <Copy className="w-3.5 h-3.5" /> Copy all
                    </button>
                    <button onClick={downloadCsv}
                        className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5"
                        data-testid="download-csv-links-btn">
                        <Download className="w-3.5 h-3.5" /> Download CSV
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-5 py-3">
                    <table className="w-full text-xs">
                        <thead className="text-gray-500 uppercase tracking-wider font-mono">
                            <tr className="border-b border-border/40">
                                <th className="text-left py-2 pr-3">Candidate</th>
                                <th className="text-left py-2 pr-3">Link</th>
                                <th className="py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-b border-border/20" data-testid={`generated-link-row-${i}`}>
                                    <td className="py-2.5 pr-3 align-top">
                                        <p className="text-sm text-white">{r.full_name}</p>
                                        <p className="text-xs text-gray-500">{r.email}</p>
                                    </td>
                                    <td className="py-2.5 pr-3 font-mono break-all">
                                        <a href={r.assessment_link} target="_blank" rel="noreferrer"
                                            className="text-emerald-300 hover:underline">
                                            {r.assessment_link}
                                        </a>
                                    </td>
                                    <td className="py-2.5 text-right whitespace-nowrap">
                                        <button onClick={() => copyOne(r.assessment_link, i)}
                                            className={`text-xs font-mono uppercase tracking-wider border px-2.5 py-1 ${copiedIdx === i ? "border-emerald-400 text-emerald-300" : "border-border/40 text-gray-300 hover:text-white"}`}
                                            data-testid={`copy-link-${i}`}>
                                            {copiedIdx === i ? "Copied" : "Copy"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-3 p-5 border-t border-border/40">
                    <button onClick={onClose}
                        className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200"
                        data-testid="generated-links-done">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
